import LZString from 'lz-string';
import type {
  ARMDetails,
  CMHCDetails,
  Country,
  LoanProgram,
  MortgageTermsData,
  MortgageType,
  PaymentFrequency,
  VariableDetails,
} from '../types/mortgageTypes';
import type { AnnualExpensesData, ExtraPaymentsData, AmortizationEntry, CalculatorTab, GDSTDSData } from '../store/calculatorStore';
import { DEFAULT_GDS_TDS } from '../store/calculatorStore';

export interface OtpEdit {
  idx: number;
  amount: number;
}

export interface ShareableTab {
  id: string;
  title: string;
  mortgageTerms: MortgageTermsData;
  annualExpenses: AnnualExpensesData;
  extraPayments: ExtraPaymentsData;
  gdsTds?: GDSTDSData;
  otpEdits?: OtpEdit[];
}

export interface ShareState {
  version: 1;
  activeTabId: string;
  tabs: ShareableTab[];
}

const DATE_FIELDS = new Set(['startDate', 'endDate']);
const MAX_SHARE_PARAM_LENGTH = 200_000;
const MAX_SHARED_TABS = 3;
const MAX_OTP_EDITS = 480;
const COUNTRIES = new Set<Country>(['USA', 'Canada']);
const MORTGAGE_TYPES = new Set<MortgageType>(['fixed', 'variable', 'adjustable']);
const LOAN_PROGRAMS = new Set<LoanProgram>(['conventional', 'fha', 'va', 'usda', 'jumbo', 'cmhc']);
const PAYMENT_FREQUENCIES = new Set<PaymentFrequency>([
  'monthly',
  'semi-monthly',
  'bi-weekly',
  'accelerated-bi-weekly',
  'weekly',
  'accelerated-weekly',
]);
const EXTRA_PAYMENT_FREQUENCIES = new Set<ExtraPaymentsData['frequency']>([
  'monthly',
  'semi-monthly',
  'bi-weekly',
  'weekly',
  'yearly',
]);

const clamp = (value: number, min: number, max: number): number =>
  Math.min(Math.max(value, min), max);

const finiteNumber = (
  value: unknown,
  fallback: number,
  min: number,
  max: number
): number => {
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? clamp(n, min, max) : fallback;
};

const validDate = (value: unknown, fallback: Date | null): Date | null => {
  if (value instanceof Date && !isNaN(value.getTime())) return value;
  if (typeof value === 'string' && value.length > 0) {
    const parsed = new Date(value);
    if (!isNaN(parsed.getTime())) return parsed;
  }
  return fallback;
};

const sanitizeExpense = (value: unknown): { type: 'percent' | 'amount'; value: number } => {
  const expense = value as { type?: unknown; value?: unknown } | undefined;
  const type = expense?.type === 'percent' ? 'percent' : 'amount';
  return {
    type,
    value: finiteNumber(expense?.value, 0, 0, type === 'percent' ? 100 : 1_000_000),
  };
};

const sanitizeMortgageTerms = (value: unknown): MortgageTermsData | null => {
  const terms = value as Partial<MortgageTermsData> | undefined;
  if (!terms) return null;

  const country = COUNTRIES.has(terms.country as Country) ? (terms.country as Country) : 'USA';
  const mortgageType = MORTGAGE_TYPES.has(terms.mortgageType as MortgageType)
    ? (terms.mortgageType as MortgageType)
    : 'fixed';
  const loanProgram = LOAN_PROGRAMS.has(terms.loanProgram as LoanProgram)
    ? (terms.loanProgram as LoanProgram)
    : 'conventional';
  const homePrice = finiteNumber(terms.homePrice, 400_000, 1, 100_000_000);
  const downPaymentAmount = finiteNumber(terms.downPaymentAmount, homePrice * 0.2, 0, homePrice);
  const downPaymentPercentage = Number(((downPaymentAmount / homePrice) * 100).toFixed(2));
  const paymentFrequency = PAYMENT_FREQUENCIES.has(terms.paymentFrequency as PaymentFrequency)
    ? (terms.paymentFrequency as PaymentFrequency)
    : 'monthly';
  const pmiType = terms.pmi?.type === 'amount' ? 'amount' : 'percent';
  const arm = terms.armDetails as Partial<ARMDetails> | undefined;
  const variable = terms.variableDetails as Partial<VariableDetails> | undefined;
  const cmhc = terms.cmhcDetails as Partial<CMHCDetails> | undefined;
  const cmhcProvince = ['BC', 'ON', 'QC', 'Other'].includes(String(cmhc?.province))
    ? (cmhc?.province as CMHCDetails['province'])
    : 'Other';

  return {
    ...terms,
    country,
    homePrice,
    downPaymentAmount,
    downPaymentPercentage,
    mortgageType,
    loanProgram,
    rate: finiteNumber(terms.rate, 5, 0, 100),
    amortizationPeriod: finiteNumber(terms.amortizationPeriod, 30, 1, 40),
    startDate: validDate(terms.startDate, new Date()) ?? new Date(),
    paymentFrequency,
    pmi: {
      type: pmiType,
      value: finiteNumber(terms.pmi?.value, 0, 0, pmiType === 'percent' ? 100 : 100_000),
    },
    armDetails: mortgageType === 'adjustable'
      ? {
          initialFixedPeriod: finiteNumber(arm?.initialFixedPeriod, 5, 1, 15),
          adjustmentFrequency: finiteNumber(arm?.adjustmentFrequency, 12, 1, 60),
          indexRate: finiteNumber(arm?.indexRate, 3.5, 0, 100),
          margin: finiteNumber(arm?.margin, 2.5, -20, 20),
          initialAdjustmentCap: finiteNumber(arm?.initialAdjustmentCap, 2, 0, 20),
          periodicCap: finiteNumber(arm?.periodicCap, 2, 0, 20),
          lifetimeCap: finiteNumber(arm?.lifetimeCap, 5, 0, 30),
        }
      : undefined,
    variableDetails: mortgageType === 'variable'
      ? {
          primeRate: finiteNumber(variable?.primeRate, country === 'Canada' ? 4.45 : 8.5, 0, 100),
          discount: finiteNumber(variable?.discount, country === 'Canada' ? 1.1 : 0.5, -50, 50),
          variableType: variable?.variableType === 'vrm' ? 'vrm' : 'arm',
          triggerRate:
            variable?.triggerRate === undefined
              ? undefined
              : finiteNumber(variable.triggerRate, 0, 0, 100),
          rateChanges: Array.isArray(variable?.rateChanges)
            ? variable.rateChanges.slice(0, 40).map((change) => ({
                year: Math.trunc(finiteNumber(change.year, 2, 1, 40)),
                delta: finiteNumber(change.delta, 0, -20, 20),
              }))
            : undefined,
        }
      : undefined,
    cmhcDetails: loanProgram === 'cmhc'
      ? {
          premiumRate: finiteNumber(cmhc?.premiumRate, 0.04, 0, 0.1),
          province: cmhcProvince,
          includeProvincialTax: Boolean(cmhc?.includeProvincialTax),
        }
      : undefined,
  };
};

const sanitizeAnnualExpenses = (value: unknown): AnnualExpensesData => {
  const expenses = value as Partial<AnnualExpensesData> | undefined;
  return {
    propertyTaxes: sanitizeExpense(expenses?.propertyTaxes),
    homeInsurance: sanitizeExpense(expenses?.homeInsurance),
    hoaUtilities: sanitizeExpense(expenses?.hoaUtilities),
  };
};

const sanitizeExtraPayments = (value: unknown): ExtraPaymentsData => {
  const extra = value as Partial<ExtraPaymentsData> | undefined;
  const frequency = EXTRA_PAYMENT_FREQUENCIES.has(extra?.frequency as ExtraPaymentsData['frequency'])
    ? (extra?.frequency as ExtraPaymentsData['frequency'])
    : 'monthly';
  return {
    frequency,
    amount: finiteNumber(extra?.amount, 0, 0, 100_000_000),
    startDate: validDate(extra?.startDate, null),
    endDate: validDate(extra?.endDate, null),
  };
};

const sanitizeOtpEdits = (value: unknown): OtpEdit[] | undefined => {
  if (!Array.isArray(value)) return undefined;
  const edits = value
    .slice(0, MAX_OTP_EDITS)
    .map((edit) => {
      const raw = edit as Partial<OtpEdit>;
      return {
        idx: Math.trunc(finiteNumber(raw.idx, -1, -1, 10_000)),
        amount: finiteNumber(raw.amount, 0, 0, 100_000_000),
      };
    })
    .filter((edit) => edit.idx >= 0 && edit.amount > 0);
  return edits.length > 0 ? edits : undefined;
};

export const sanitizeGdsTds = (value: unknown): GDSTDSData => {
  const raw = value as Partial<GDSTDSData> | undefined;
  return {
    monthlyIncome: finiteNumber(raw?.monthlyIncome, DEFAULT_GDS_TDS.monthlyIncome, 0, 1_000_000),
    heatingCosts: finiteNumber(raw?.heatingCosts, DEFAULT_GDS_TDS.heatingCosts, 0, 100_000),
    condoFees: finiteNumber(raw?.condoFees, DEFAULT_GDS_TDS.condoFees, 0, 100_000),
    otherDebts: finiteNumber(raw?.otherDebts, DEFAULT_GDS_TDS.otherDebts, 0, 1_000_000),
  };
};

const gdsTdsEqualsDefaults = (gds: GDSTDSData): boolean =>
  gds.monthlyIncome === DEFAULT_GDS_TDS.monthlyIncome &&
  gds.heatingCosts === DEFAULT_GDS_TDS.heatingCosts &&
  gds.condoFees === DEFAULT_GDS_TDS.condoFees &&
  gds.otherDebts === DEFAULT_GDS_TDS.otherDebts;

const sanitizeShareState = (value: unknown): ShareState | null => {
  const state = value as Partial<ShareState> | undefined;
  if (state?.version !== 1 || !Array.isArray(state.tabs)) return null;

  const tabs = state.tabs.slice(0, MAX_SHARED_TABS).flatMap((tab, index) => {
    const sharedTab = tab as Partial<ShareableTab>;
    const mortgageTerms = sanitizeMortgageTerms(sharedTab.mortgageTerms);
    if (!mortgageTerms) return [];
    const id = typeof sharedTab.id === 'string' && sharedTab.id.trim()
      ? sharedTab.id.slice(0, 80)
      : `shared-${index + 1}`;
    return [{
      id,
      title: typeof sharedTab.title === 'string' && sharedTab.title.trim()
        ? sharedTab.title.slice(0, 24)
        : `CALC ${index + 1}`,
      mortgageTerms,
      annualExpenses: sanitizeAnnualExpenses(sharedTab.annualExpenses),
      extraPayments: sanitizeExtraPayments(sharedTab.extraPayments),
      ...(sharedTab.gdsTds !== undefined
        ? { gdsTds: sanitizeGdsTds(sharedTab.gdsTds) }
        : {}),
      otpEdits: sanitizeOtpEdits(sharedTab.otpEdits),
    }];
  });

  if (tabs.length === 0) return null;
  // "compare" is a virtual tab id (not listed in tabs). Without this branch, shares that
  // encode Compare as active incorrectly fall back to the first scenario tab after sanitize.
  const wantsCompare = state.activeTabId === 'compare' && tabs.length >= 2;
  const activeTabId = wantsCompare
    ? 'compare'
    : tabs.some((tab) => tab.id === state.activeTabId)
      ? (state.activeTabId as string)
      : tabs[0].id;

  return {
    version: 1,
    activeTabId,
    tabs,
  };
};

function dateReviver(_key: string, value: unknown): unknown {
  if (DATE_FIELDS.has(_key) && typeof value === 'string' && value.length > 0) {
    const date = new Date(value);
    return isNaN(date.getTime()) ? null : date;
  }
  return value;
}

export function extractOtpEdits(schedule: AmortizationEntry[]): OtpEdit[] {
  return schedule
    .map((entry, idx) => ({ idx, amount: entry.oneTimePayment ?? 0 }))
    .filter(e => e.amount !== 0);
}

export function serializeState(tabs: CalculatorTab[], activeTabId: string): string {
  const shareState: ShareState = {
    version: 1,
    activeTabId,
    tabs: tabs.map(tab => ({
      id: tab.id,
      title: tab.title,
      mortgageTerms: tab.data.mortgageTerms,
      annualExpenses: tab.data.annualExpenses,
      extraPayments: tab.data.extraPayments,
      ...(tab.data.mortgageTerms.country === 'Canada' && !gdsTdsEqualsDefaults(tab.data.gdsTds)
        ? { gdsTds: tab.data.gdsTds }
        : {}),
      ...(tab.data.hasManualExtraPaymentEdits && tab.data.amortizationSchedule.length > 0
        ? { otpEdits: extractOtpEdits(tab.data.amortizationSchedule) }
        : {}),
    })),
  };
  const json = JSON.stringify(shareState);
  return LZString.compressToEncodedURIComponent(json);
}

export function deserializeState(encoded: string): ShareState | null {
  try {
    if (encoded.length > MAX_SHARE_PARAM_LENGTH) return null;
    const json = LZString.decompressFromEncodedURIComponent(encoded);
    if (!json) return null;
    const parsed = JSON.parse(json, dateReviver);
    return sanitizeShareState(parsed);
  } catch {
    return null;
  }
}

export function generateShareUrl(tabs: CalculatorTab[], activeTabId: string): string {
  const encoded = serializeState(tabs, activeTabId);
  const url = new URL(window.location.href);
  url.search = '';
  url.hash = '';
  url.searchParams.set('s', encoded);
  return url.toString();
}

/** Returns whether the text was copied. Uses a hidden textarea fallback when the Clipboard API is unavailable. */
export async function copyTextToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      ta.style.position = 'fixed';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      return ok;
    } catch {
      return false;
    }
  }
}
